import { useInfiniteQueryBuilder, useQueryBuilder, useMutationBuilder } from "../useBuilders";
import {
  DEAL_INSIGHTS_ACTIVITIES,
  DEAL_INSIGHTS_DETAIL,
  DEAL_INSIGHTS_FILTER_OPTIONS,
  DEAL_INSIGHTS_SUMMARY,
  DEAL_INSIGHTS_LIST,
  DEAL_INSIGHTS_TIMELINE,
  FETCH,
  DEAL_INSIGHTS_SYNC,
  UPDATE,
  DEAL_INSIGHTS,
  INVALIDATE,
  DEAL_DETAILS
} from "../queryKeys";
import { dealInsightsApi } from "src/services/dealInsights/dealInsights";
import { ActivitiesRequestPayload, DealsRequestPayload } from "src/types/dealInsights";

// deal summary
export const useDealInsightsSummary = () => {
  return useQueryBuilder([DEAL_INSIGHTS, DEAL_INSIGHTS_SUMMARY, FETCH], dealInsightsApi.getSummary);
};

// filter options
export const useDealFilterOptions = () => {
  return useQueryBuilder([DEAL_INSIGHTS, DEAL_INSIGHTS_FILTER_OPTIONS, FETCH], dealInsightsApi.getFilterOptions, false, 5 * 60 * 1000, // filter options are stable — cache for 5 min
  );
};

// list of deals
export const useDealsList = (payload: DealsRequestPayload) => {
  return useInfiniteQueryBuilder(
    [DEAL_INSIGHTS, DEAL_INSIGHTS_LIST, FETCH, JSON.stringify(payload)],
    ({ pageParam = 0 }) => dealInsightsApi.getDeals({ ...payload, page: pageParam }),
    0,
    (response) => {
      const nextPage = (response?.totalPages > response?.page) ? response?.page + 1 : undefined;
      return nextPage;
    },
    (firstPage, allPages) => "",
    true,
    false,
  );
};

// deal activities
export const useDealActivities = (payload: ActivitiesRequestPayload | null, enable: boolean) => {
  return useInfiniteQueryBuilder(
    [INVALIDATE, DEAL_DETAILS, DEAL_INSIGHTS_ACTIVITIES, FETCH, payload ? JSON.stringify(payload) : ""],
    ({ pageParam = 0 }) => payload ? dealInsightsApi.getActivities({ ...payload, page: pageParam }) : Promise.resolve({}),
    0,
    (response) => {
      const nextPage = (response?.totalPages > response?.page) ? response?.page + 1 : undefined;
      return nextPage;
    },
    (firstPage, allPages) => "",
    enable,
    false,
  );
};

// deal details
export const useDealDetail = (dealId?: string) => {
  return useQueryBuilder(
    [INVALIDATE, DEAL_DETAILS, DEAL_INSIGHTS_DETAIL, FETCH, dealId ?? ""],
    () => dealInsightsApi.getDealDetail({ 'dealId': dealId ?? "" }),
    false,
    1 * 60 * 1000,
    false,
    undefined,
    !!dealId,
  );
};

// timeline
export const useDealTimeline = (dealId: string) => {
  return useQueryBuilder(
    [INVALIDATE, DEAL_DETAILS, DEAL_INSIGHTS_TIMELINE, FETCH, dealId ?? ''],
    () => dealInsightsApi.getTimeline({ 'dealExternalId': dealId ?? "" }),
    false,
    1 * 60 * 1000,
    false,
    undefined,
    !!dealId
  );
};

// sync deal
export const useSyncDeal = () => {
  return useMutationBuilder([DEAL_INSIGHTS_SYNC, UPDATE], dealInsightsApi.syncDeal, [INVALIDATE, DEAL_DETAILS]);
};
